import { useAppDispatch, useAppSelector } from "features/config/hooks";

import { selectGetProductData } from "../slices/get-product-data.slice";
import {
  openMessageModal,
  closeMessageModal,
} from "features/shared/presentation/slices/message-modal.slice";
import { useParams } from "react-router-dom";
import {
  ActionHeader,
  BannerIndicator,
  CancelAndConfirm,
  isQuantityEmpty,
  useActionAvailability,
} from "./stock-ordering-utils";
import { stockDeliveryApproval } from "../slices/stock-delivery-approval.slice";
import { REACT_APP_DOMAIN_URL } from "features/shared/constants";
import { UploadMultipleImageModal } from "../modals";
import {
  closeModalScroll,
  toggleModalScroll,
} from "../slices/modalScrollSlice.slice";
import { GetProductDataModel } from "features/stock-ordering/core/domain/get-product-data.model";
import { useCallback, useState } from "react";
import dayjs from "dayjs";
import { CheckCircle, CircleCheck, Download, FileText } from "lucide-react";

interface StockOrderProcessStoreManagerDeliveryRecieveOrderApprovalProps {
  onClose: (close: boolean) => void;
}

interface PaymentTermsProps {
  name: string;
  hasAccess: boolean;
  order_information?: GetProductDataModel["order_information"] | undefined;
  handleCancelledOrder?: () => void;
  handleConfirm: (comDeliveryDate?: string | null | undefined) => void;
  customButtons?: {
    label: string;
    onClick: () => void;
    position?: "start" | "middle" | "end";
    disabled?: boolean;
  }[];
  fileData?: {
    files: File[] | null;
    handleRemoveFile: () => void;
    handleOpenFileModal: () => void;
  };

  bannerIndicatorSettings?: {
    visible: boolean;
    content: string;
    textColor: string;
    bgColor: string;
  };
}

export function StockOrderProcessDeliveryRecieveApproval(
  props: StockOrderProcessStoreManagerDeliveryRecieveOrderApprovalProps
) {
  /**
   * url param that contains order id
   */
  const { orderId } = useParams();

  /**
   * dispatch and selector user for sending and fetching request
   */
  const dispatch = useAppDispatch();
  const getProductDataState = useAppSelector(selectGetProductData);

  const { processId, actionAvailable } = useActionAvailability();
  const managerAccess = useActionAvailability(3).permission;
  const supplierAccess = useActionAvailability(5).permission;
  const dispatcherAccess = useActionAvailability(6).permission;

  const [uploadedPayment, setUploadedFile] = useState<File[] | null>(null);
  const [hasPreviewed, setHasPreviewed] = useState(false);

  const [openUploadModal, setOpenUploadModal] = useState(false);
  /**
   * Destructure product data
   */
  const { order_information, product_data } = getProductDataState.data ?? {
    product_data: [],
  };

  const modalMessage = (action: {
    type: "approve" | "reject" | "upload-payment" | "awknowledge-payment";
    role: "manager" | "supplier" | "multim";
  }) => {
    if (isQuantityEmpty(product_data, processId)) {
      switch (action.role + "-" + action.type) {
        case "manager-approve":
          return "<b>Confirm Approval Despite Quantity Inconsistencies?</b><br><br> There are discrepancies between the dispatched and delivered quantities for this order. Are you sure you want to <b>approve</b> the order despite these inconsistencies?<br>";
        case "manager-reject":
          return "<b>Reject Order Due to Quantity Inconsistencies</b><br><br> This order contains mismatched quantities between what was dispatched and what was delivered. Are you sure you want to <b>reject</b> the order and request a correction?";
        case "supplier-approve":
          return "<b>There are inconsistencies between the dispatch and delivery quantities.</b><br><br> Do you still wish to approve the order despite the delivery mismatch?<br>";
        case "supplier-reject":
          return "<b>Inconsistencies were found between the dispatched and delivered quantities.</b><br><br> Are you certain you want to reject this order based on the identified mismatch?";

        default:
          return "Problem encountered. Please click No.";
      }
    } else {
      switch (action.role + "-" + action.type) {
        case "manager-approve":
          return "<b>Are you sure you want to approve this order?</b><br><br> By confirming, you acknowledge that all order details have been reviewed and are accurate. The process will proceed to supplier verification.<br>";
        case "manager-reject":
          return "<b>Are you sure you want to reject this order?</b><br><br> By rejecting, you indicate that there are issues or discrepancies in the order that require correction before it can proceed.<br>";
        case "supplier-approve":
          return "<b>Do you confirm that the order details are accurate and complete?.</b><br><br> Approving this order means you acknowledge all quantities and information are correct?<br>";
        case "supplier-reject":
          return "<b>Are you sure you want to reject this order?</b><br><br> Please ensure the rejection is due to valid concerns and consider providing clarification if needed.";
        case "multim-upload-payment":
          return `<b>Please review the payment details before proceeding. Is the uploaded proof of payment correct and valid?</b>
                  <br><br>
                  By clicking <b>YES</b>, you acknowledge that payment was submitted on 
                  <b>${dayjs(new Date()).format(
                    "llll"
                  )}</b>. This confirmation serves as proof of payment submission and acknowledgment of the transaction. 
                  <br><br>
                  By confirming, you also agree to our 
                  <a href="/terms-and-condition" target="_blank"><b>Terms & Conditions</b></a>.
                  `;
        case "supplier-awknowledge-payment":
          return `<b>Please carefully review the payment details before proceeding.</b>
                  <br><br>
                  By clicking <b>YES</b>, you acknowledge that the payment was submitted on 
                  <b>${dayjs(new Date()).format(
                    "llll"
                  )}</b>. This confirmation serves as an official acknowledgment of the payment transaction. 
                  <br><br>
                  You also confirm that you have read and agreed to our 
                  <a href="/terms-and-condition" target="_blank"><b>Terms & Conditions</b></a>, and understand that the company shall not be held liable for any issues arising from the transaction. 
                  <br><br>
                  Please note that this acknowledgment is <b>irrevocable</b> and binding, and once confirmed, it cannot be reversed.`;

        default:
          return "Problem encountered. Please click No.";
      }
    }
  };

  const handleButtonClick =
    (role: "manager" | "supplier" | "multim") =>
    (action: "approve" | "reject" | "upload-payment" | "awknowledge-payment") =>
    () => {
      if (!orderId) return;

      dispatch(
        openMessageModal({
          useHtml: true,
          message: modalMessage({ role: role, type: action }) ?? "",
          buttons: [
            {
              text: "Yes",
              color: "#CC5801",
              onClick: () => {
                // const multimOrdering =
                //   order_information?.companyId === 1
                //     ? undefined
                //     : isQuantityEmpty(product_data, processId);

                dispatch(
                  stockDeliveryApproval({
                    orderId: orderId,
                    role: role,
                    action: action,
                    data: {
                      // withDiscrepancy: multimOrdering,
                      uploadedPayment: uploadedPayment?.[0] ?? undefined,
                    },
                  })
                );

                // if (
                //   isQuantityEmpty(product_data, processId) &&
                //   action === "approve" &&
                //   role === "manager"
                // ) {
                //   const link = `${REACT_APP_DOMAIN_URL}api/stock/delivery-adjustment/${orderId}/delivery-adjustment`;
                //   window.open(link, "_blank");
                // }

                dispatch(closeMessageModal());
                props.onClose(true);
              },
            },
            {
              text: "No",
              color: "#22201A",
              onClick: () => {
                dispatch(closeMessageModal());
              },
            },
          ],
        })
      );
    };

  const handleSetFile = (file: File[] | null) => setUploadedFile(file);

  const handleRemoveFile = useCallback(() => {
    setUploadedFile(null);
  }, [uploadedPayment]);

  const handleOpenFileModal = useCallback(() => {
    setOpenUploadModal(true);
    dispatch(toggleModalScroll());
  }, [openUploadModal]);

  const handleFilePreview = () => {
    window.open(
      `${REACT_APP_DOMAIN_URL}api/stock/ordered/download_file_information/${order_information?.payment_detail_image}`,
      "_blank"
    );
    setHasPreviewed(true);
  };

  return (
    <>
      {order_information?.deliveryReceieveApprovalDate === null &&
        order_information.companyId !== 1 && (
          <>
            <ActionHeader
              primary={"Manager's Approval"}
              secondary="This action requires review and confirmation from your manager before it can proceed."
              HeaderIcon={CircleCheck}
            />
            <DeliveryReceiveManagerApproval
              handleDeliveryRecieveApproval={handleButtonClick("manager")}
              visible={!!(actionAvailable && managerAccess)}
              withReject={
                order_information.deliveryReceiveSupplierRejectDate !== null
              }
            />
          </>
        )}

      {order_information?.supplierApprovalDate === null &&
        order_information.companyId !== 1 &&
        order_information?.deliveryReceieveApprovalDate !== null && (
          <>
            <ActionHeader
              primary={"Supplier's Approval"}
              secondary="This step requires the supplier to verify and confirm the order details before it can be completed."
              HeaderIcon={CircleCheck}
            />
            <DeliveryReceiveSupplierApproval
              handleDeliveryRecieveApproval={handleButtonClick("supplier")}
              visible={
                (actionAvailable && (supplierAccess || dispatcherAccess)) ??
                false
              }
            />
          </>
        )}

      {/**
       * As long as status is not complete customer can upload payment image
       */}
      {!Boolean(order_information?.isPayFirst) &&
        order_information?.companyId === 1 && (
          <>
            <ActionHeader
              primary={"Payment Terms"}
              secondary={
                hasPreviewed
                  ? "File has been reviewed. You may now acknowledge payment."
                  : "Download and review this file before acknowledging payment."
              }
              HeaderIcon={CircleCheck}
            />

            {order_information?.status_id !== 10 &&
            order_information?.companyId === 1 &&
            (!supplierAccess || !dispatcherAccess) ? (
              <MultimUploadPaymentTerms
                name="upload-payment"
                hasAccess={order_information?.companyId === 1}
                order_information={order_information}
                handleConfirm={handleButtonClick("multim")}
                bannerIndicatorSettings={{
                  visible: true,

                  content: "You order is waiting payment.",
                  textColor: "text-[#4A4A4A]",
                  bgColor: "bg-[#AEE1F7]",
                }}
                fileData={{
                  files: uploadedPayment,
                  handleOpenFileModal,
                  handleRemoveFile,
                }}
              />
            ) : (
              <>
                <p className="text-sm text-gray-500">
                  {hasPreviewed
                    ? "File has been reviewed. You may now acknowledge payment."
                    : "Download and review this file before acknowledging payment."}
                </p>

                {!hasPreviewed ? (
                  <button
                    onClick={handleFilePreview}
                    className="w-full flex items-center justify-center px-4 py-2 bg-blue-600 text-white rounded-xl shadow hover:bg-blue-700 transition"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Preview / Download File
                  </button>
                ) : (
                  <div className="flex justify-between space-x-5">
                    {/* Re-download */}
                    <a
                      href={`${REACT_APP_DOMAIN_URL}api/stock/ordered/download_file_information/${order_information?.payment_detail_image}`}
                      target="_blank"
                      className="w-full flex basis-[72%] items-center justify-center px-4 py-2 bg-gray-100 text-gray-700 rounded-xl shadow hover:bg-gray-200 transition"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Download Again
                    </a>

                    {/* Acknowledge */}
                    <CancelAndConfirm
                      visible
                      handleConfirm={handleButtonClick("supplier")(
                        "awknowledge-payment"
                      )}
                      confirmButtonName="Acknowledge Payment"
                    />
                  </div>
                )}

                {hasPreviewed && (
                  <div className="flex items-center mt-4 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    <span>
                      Reviewed on {dayjs().format("MMM D, YYYY h:mm A")}
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}

      <UploadMultipleImageModal
        open={openUploadModal}
        onClose={() => {
          setOpenUploadModal(false);
          setTimeout(() => {
            dispatch(closeModalScroll());
          }, 500);
        }}
        single
        files={uploadedPayment}
        setFiles={handleSetFile}
      />
    </>
  );
}

/**
 * @author knhrvs - kenoresca
 *
 * Manager approval
 * -----------------------------------------------------
 *
 * @param handleDeliveryRecieveApproval - handles on approval button click
 * @param visible - hides approval buttons
 * @returns React Element - manager approval button component
 */

interface DeliveryReceiveManagerApprovalProps {
  handleDeliveryRecieveApproval: (action: "approve" | "reject") => () => void;
  visible: boolean;
  withReject: boolean;
}

const DeliveryReceiveManagerApproval = (
  props: DeliveryReceiveManagerApprovalProps
) => {
  const {
    handleDeliveryRecieveApproval,
    visible = true,
    withReject = false,
  } = props;

  if (visible) {
    return (
      <CancelAndConfirm
        visible
        handleCancelledOrder={handleDeliveryRecieveApproval("reject")}
        handleConfirm={handleDeliveryRecieveApproval("approve")}
        confirmButtonName="Approve order"
        cancelButtonName="Reject order"
      />
    );
  }

  if (!visible && !withReject) {
    return (
      <BannerIndicator
        visible
        content="The order has been delivered and is currently pending the store manager’s confirmation. Once approved, 
        the process will proceed to the supplier for final verification."
        textColor="text-[#4A4A4A]"
        bgColor="bg-[#AEE1F7]"
      />
    );
  }

  if (withReject) {
    return (
      <BannerIndicator
        visible
        content="The order has been rejected by supplier and is currently pending the store manager’s confirmation. Once approved, 
        the process will proceed to the supplier for final verification."
        textColor="text-[#4A4A4A]"
        bgColor="bg-[#AEE1F7]"
      />
    );
  }

  return null;
};

/**
 * @author knhrvs - kenoresca
 *
 * Supplier approval
 * -----------------------------------------------------
 *
 * @param handleDeliveryRecieveApproval - handles on approval button click
 * @param hasAccess - hides approval buttons
 * @returns React Element - supplier approval button component
 */

interface DeliveryReceiveSupplierApprovalProps {
  handleDeliveryRecieveApproval: (action: "approve" | "reject") => () => void;
  visible: boolean;
}
const DeliveryReceiveSupplierApproval = (
  props: DeliveryReceiveSupplierApprovalProps
) => {
  const { handleDeliveryRecieveApproval, visible = true } = props;

  if (visible) {
    return (
      <CancelAndConfirm
        visible
        handleCancelledOrder={handleDeliveryRecieveApproval("reject")}
        handleConfirm={handleDeliveryRecieveApproval("approve")}
        confirmButtonName="Approve order"
        cancelButtonName="Reject order"
      />
    );
  }

  if (!visible) {
    return (
      <BannerIndicator
        visible
        content="Waiting for the supplier's final verification. Once confirmed, the order will be marked as complete. 
        Please wait while the supplier reviews and approves the delivery."
        textColor="text-[#4A4A4A]"
        bgColor="bg-[#AEE1F7]"
      />
    );
  }

  return null;
};

/**
 *
 * @param param0
 * @returns React element - upload payment
 */

const MultimUploadPaymentTerms = ({
  handleConfirm,
  hasAccess,
  bannerIndicatorSettings = {
    visible: false,
    content: "Problem encountered",
    textColor: "text-[#4A4A4A]",
    bgColor: "bg-red-300",
  },
  fileData,
  deadlineDays,
}: {
  name: string;
  hasAccess: boolean;
  order_information?: GetProductDataModel["order_information"] | undefined;
  handleCancelledOrder?: () => void;
  handleConfirm: (type: "upload-payment") => () => void;
  customButtons?: {
    label: string;
    onClick: () => void;
    position?: "start" | "middle" | "end";
    disabled?: boolean;
  }[];
  fileData?: {
    files: File[] | null;
    handleRemoveFile: () => void;
    handleOpenFileModal: () => void;
  };
  bannerIndicatorSettings?: {
    visible: boolean;
    content: string;
    textColor: string;
    bgColor: string;
  };
  deadlineDays?: number;
}) => {
  const { files, handleOpenFileModal, handleRemoveFile } = fileData ?? {};
  if (hasAccess) {
    return (
      <>
        <div className="flex flex-col lg:flex-row justify-between gap-4">
          <div className="w-full lg:w-1/2 space-y-3">
            {(files?.length === 0 || files === null) && (
              <button
                onClick={handleOpenFileModal}
                className="group w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium text-sm rounded-lg py-2.5 px-4 transition-all duration-200 transform hover:scale-[1.01] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <svg
                  className="w-4 h-4 transition-transform group-hover:scale-105"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                  />
                </svg>
                Upload Payment
              </button>
            )}

            {files && files.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-3 hover:shadow-sm transition-all duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4"
                        />
                      </svg>
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className="text-sm text-gray-900 font-medium block truncate">
                        {files[0].name}
                      </span>
                      <span className="text-xs text-green-600 font-medium">
                        Successfully uploaded
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={handleRemoveFile}
                    className="ml-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all duration-200"
                    title="Remove file"
                  >
                    <svg
                      className="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="w-full lg:w-auto lg:flex-shrink-0">
            <CancelAndConfirm
              handleConfirm={handleConfirm("upload-payment")}
              visible={bannerIndicatorSettings.visible}
              disableConfirm={files?.length === 0}
            />
          </div>
        </div>
      </>
    );
  }

  if (!hasAccess) {
    return <BannerIndicator {...bannerIndicatorSettings} />;
  }

  return null;
};
